import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useGetTaxonomiesByVersion } from '@/hooks/useGetTaxonomiesByVersion';
import { useGetTaxonomiesVersions } from '@/hooks/useGetTaxonomiesVersions';
import { useGetTaxonomiesByVersionAndCategory } from '@/hooks/useGetTaxonomyPropertiesByVersionAndCategory';
import {
  TaxonomiesUpdatedResponse,
  TaxonomyGetResponse,
} from '@/services/TaxonomiesService';

interface TaxonomyContextValue {
  selectedCategory: string;
  setSelectedCategory: Dispatch<SetStateAction<string>>;
  taxonomyPropertiesUpdated: TaxonomiesUpdatedResponse | null;
  setTaxonomyPropertiesUpdated: Dispatch<
    SetStateAction<TaxonomiesUpdatedResponse | null>
  >;
  selectedVersion: string;
  setSelectedVersion: Dispatch<SetStateAction<string>>;
  setMainVersion: Dispatch<SetStateAction<string>>;
  mainVersion: string;
  versions: string[] | undefined;
  sortedCategories: string[] | undefined;
  properties: TaxonomyGetResponse[];
  propertiesLoading: boolean;
}

export const TaxonomyContext = createContext({} as TaxonomyContextValue);

export function TaxonomyProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { versions } = useGetTaxonomiesVersions();

  const [mainVersion, setMainVersion] = useState(
    user?.customer?.taxonomyVersion || '',
  );
  const initialVersion = mainVersion || versions?.[versions.length - 1] || '';
  const [selectedVersion, setSelectedVersion] = useState(initialVersion);

  const { categories } = useGetTaxonomiesByVersion(selectedVersion);
  const sortedCategories = useMemo(
    () =>
      categories?.sort((a, b) =>
        a.localeCompare(b, navigator.language || 'en-US', {
          sensitivity: 'base',
        }),
      ),
    [categories],
  );
  const inicialCategory = sortedCategories?.[0] || '';
  const [selectedCategory, setSelectedCategory] = useState(inicialCategory);

  const { properties, propertiesLoading } =
    useGetTaxonomiesByVersionAndCategory(
      selectedVersion || initialVersion,
      selectedCategory || inicialCategory,
    );

  const [taxonomyPropertiesUpdated, setTaxonomyPropertiesUpdated] =
    useState<TaxonomiesUpdatedResponse | null>({
      version: selectedVersion,
      data: [],
    });

  return (
    <TaxonomyContext.Provider
      value={{
        sortedCategories,
        selectedCategory,
        setSelectedCategory,
        taxonomyPropertiesUpdated,
        setTaxonomyPropertiesUpdated,
        selectedVersion,
        setSelectedVersion,
        setMainVersion,
        mainVersion,
        versions,
        properties,
        propertiesLoading,
      }}
    >
      {children}
    </TaxonomyContext.Provider>
  );
}

export function useTaxonomyContext() {
  return useContext(TaxonomyContext);
}
